export const integrationCategories = [
  { id: "payments", name: "Payments", icon: "💳" },
  { id: "crm", name: "CRM", icon: "🤝" },
  { id: "productivity", name: "Productivity", icon: "📊" },
  { id: "communication", name: "Communication", icon: "💬" },
  { id: "development", name: "Development", icon: "🛠" },
  { id: "content", name: "Content", icon: "📝" },
];

export const integrations = [
  {
    id: "stripe",
    name: "Stripe",
    category: "payments",
    tagline: "Payments and subscriptions",
    description:
      "Accept online payments, manage subscriptions, and process recurring charges with the most complete payment platform on the market. Native integration with real-time webhooks.",
    features: [
      "Credit and debit card processing",
      "Subscriptions and recurring billing",
      "Real-time webhooks for payment events",
      "Support for 135+ currencies",
      "Integrated transaction dashboard",
      "Stripe Radar fraud protection",
    ],
    setupSteps: [
      "Create your account at stripe.com",
      "Get your API keys (Publishable + Secret)",
      "Paste the key in the configuration modal",
      "Configure webhooks to receive events",
      "Test with test cards in sandbox mode",
    ],
    brandColor: "#635BFF",
    icon: "CreditCard",
    status: "connected",
    connectedAt: "2026-05-15T10:30:00Z",
    configFields: [
      { id: "publishable_key", label: "Publishable Key", type: "text", placeholder: "pk_live_..." },
      { id: "secret_key", label: "Secret Key", type: "password", placeholder: "sk_live_..." },
      { id: "webhook_secret", label: "Webhook Signing Secret", type: "password", placeholder: "whsec_..." },
    ],
  },
  {
    id: "shopify",
    name: "Shopify",
    category: "payments",
    tagline: "Complete e-commerce",
    description:
      "Connect your Shopify store to manage products, orders, and customers directly from Max OS Cloud. Automate sales flows and customer support.",
    features: [
      "Product and inventory sync",
      "Automated order management",
      "New order notifications",
      "E-commerce support chatbot",
      "Integrated sales reports",
    ],
    setupSteps: [
      "Access the Shopify Partners dashboard",
      "Create a custom app",
      "Get the Storefront Access Token",
      "Configure the necessary permissions",
      "Enter credentials in the modal",
    ],
    brandColor: "#96BF48",
    icon: "ShoppingCart",
    status: "available",
    configFields: [
      { id: "store_url", label: "Store URL", type: "text", placeholder: "your-store.myshopify.com" },
      { id: "access_token", label: "Admin API Access Token", type: "password", placeholder: "shpat_..." },
      { id: "api_version", label: "API Version", type: "text", placeholder: "2024-01" },
    ],
  },
  {
    id: "hubspot",
    name: "HubSpot",
    category: "crm",
    tagline: "CRM and marketing",
    description:
      "Integrate your HubSpot CRM to manage contacts, deals, and marketing campaigns. Automate follow-ups and lead qualification with AI.",
    features: [
      "Bidirectional contact sync",
      "Sales pipeline management",
      "Marketing automation",
      "AI-powered lead scoring",
      "Campaign performance reports",
    ],
    setupSteps: [
      "Create a HubSpot developer account",
      "Create an app in the Developer Portal",
      "Get the Client ID and Client Secret",
      "Configure the necessary scopes",
      "Authorize the OAuth connection",
    ],
    brandColor: "#FF7A59",
    icon: "Users",
    status: "available",
    configFields: [
      { id: "client_id", label: "Client ID", type: "text" },
      { id: "client_secret", label: "Client Secret", type: "password" },
      { id: "redirect_uri", label: "Redirect URI", type: "text", placeholder: "https://..." },
    ],
  },
  {
    id: "salesforce",
    name: "Salesforce",
    category: "crm",
    tagline: "Enterprise CRM",
    description:
      "Connect to Salesforce to manage accounts, opportunities, and support cases at enterprise scale. Robust API with custom object support.",
    features: [
      "Account and contact management",
      "Opportunity pipeline",
      "Workflow automation",
      "Custom reports and dashboards",
      "Custom object support",
    ],
    setupSteps: [
      "Go to Setup > App Manager in Salesforce",
      "Create a Connected App",
      "Enable OAuth Settings",
      "Get Consumer Key and Secret",
      "Configure the necessary OAuth scopes",
    ],
    brandColor: "#00A1E0",
    icon: "Building2",
    status: "available",
    configFields: [
      { id: "client_id", label: "Consumer Key", type: "text" },
      { id: "client_secret", label: "Consumer Secret", type: "password" },
      { id: "instance_url", label: "Instance URL", type: "text", placeholder: "https://yourinstance.salesforce.com" },
      { id: "username", label: "Username", type: "text" },
      { id: "password", label: "Password + Security Token", type: "password" },
    ],
  },
  {
    id: "notion",
    name: "Notion",
    category: "productivity",
    tagline: "Knowledge base and docs",
    description:
      "Sync your Notion pages and databases to create a living knowledge base. Documentation, wikis, and project management in one place.",
    features: [
      "Page and database sync",
      "Automatic documentation creation",
      "Knowledge base templates",
      "Semantic content search",
      "Document versioning",
    ],
    setupSteps: [
      "Go to notion.so/my-integrations",
      "Create a new integration",
      "Copy the Internal Integration Token",
      "Share pages with the integration",
      "Paste the token in the configuration modal",
    ],
    brandColor: "#000000",
    icon: "FileText",
    status: "available",
    configFields: [
      { id: "token", label: "Internal Integration Token", type: "password", placeholder: "secret_..." },
      { id: "database_id", label: "Default Database ID", type: "text", placeholder: "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" },
    ],
  },
  {
    id: "airtable",
    name: "Airtable",
    category: "productivity",
    tagline: "Flexible database",
    description:
      "Connect your Airtable bases to manage structured data with the flexibility of a spreadsheet and the power of a database.",
    features: [
      "Base and table sync",
      "Automatic record CRUD",
      "Synced filters and views",
      "Change-based automation",
      "Attachment and link support",
    ],
    setupSteps: [
      "Go to airtable.com/create/tokens",
      "Generate a Personal Access Token",
      "Define the necessary scopes",
      "Identify the desired Base ID",
      "Enter credentials in the modal",
    ],
    brandColor: "#18BFFF",
    icon: "Database",
    status: "available",
    configFields: [
      { id: "api_key", label: "Personal Access Token", type: "password", placeholder: "pat..." },
      { id: "base_id", label: "Base ID", type: "text", placeholder: "app..." },
      { id: "table_name", label: "Table Name", type: "text" },
    ],
  },
  {
    id: "trello",
    name: "Trello",
    category: "productivity",
    tagline: "Project management",
    description:
      "Manage Trello boards, lists, and cards directly from Max OS Cloud. Automate card movement and create progress reports.",
    features: [
      "Board and card sync",
      "Automatic card creation",
      "Movement between lists",
      "Deadline notifications",
      "Progress reports",
    ],
    setupSteps: [
      "Go to trello.com/app-key",
      "Get your API Key",
      "Generate an access token",
      "Set the token duration",
      "Enter Key and Token in the modal",
    ],
    brandColor: "#0079BF",
    icon: "LayoutGrid",
    status: "available",
    configFields: [
      { id: "api_key", label: "API Key", type: "text" },
      { id: "token", label: "Token", type: "password" },
      { id: "board_id", label: "Default Board ID", type: "text" },
    ],
  },
  {
    id: "slack",
    name: "Slack",
    category: "communication",
    tagline: "Team communication",
    description:
      "Connect your Slack workspaces to send notifications, create channels, and interact with your team directly from your AI agents.",
    features: [
      "Send messages to channels",
      "Real-time event notifications",
      "Automatic thread creation",
      "Custom slash commands",
      "Interactions with modals and buttons",
    ],
    setupSteps: [
      "Go to api.slack.com/apps",
      "Create a new app",
      "Add Bot Token Scopes",
      "Install the app in the workspace",
      "Copy the Bot User OAuth Token",
    ],
    brandColor: "#4A154B",
    icon: "MessageSquare",
    status: "connected",
    connectedAt: "2026-05-20T14:00:00Z",
    configFields: [
      { id: "bot_token", label: "Bot User OAuth Token", type: "password", placeholder: "xoxb-..." },
      { id: "signing_secret", label: "Signing Secret", type: "password" },
      { id: "default_channel", label: "Default Channel", type: "text", placeholder: "#general" },
    ],
  },
  {
    id: "zapier",
    name: "Zapier",
    category: "communication",
    tagline: "Universal connector",
    description:
      "Connect over 5,000 apps through Zapier. Automate workflows between services that don't have a direct integration.",
    features: [
      "Connection with 5,000+ apps",
      "Pre-configured Zaps for common cases",
      "Custom triggers and actions",
      "Filters and data formatting",
      "Multi-step Zaps with conditional logic",
    ],
    setupSteps: [
      "Go to zapier.com/app/developer",
      "Create a new integration",
      "Define triggers and actions",
      "Get the API Key",
      "Configure in the Max OS Cloud modal",
    ],
    brandColor: "#FF4A00",
    icon: "Zap",
    status: "available",
    configFields: [
      { id: "api_key", label: "API Key", type: "password" },
      { id: "webhook_url", label: "Webhook URL", type: "text", placeholder: "https://hooks.zapier.com/..." },
    ],
  },
  {
    id: "github",
    name: "GitHub",
    category: "development",
    tagline: "Development",
    description:
      "Manage repositories, issues, PRs, and GitHub actions. Automate code reviews, deploys, and CI/CD notifications.",
    features: [
      "Issue and pull request management",
      "CI/CD notifications",
      "AI-assisted code review",
      "Deploy automation",
      "Release and tag management",
    ],
    setupSteps: [
      "Go to Settings > Developer settings",
      "Create a Personal Access Token",
      "Define the necessary scopes",
      "For GitHub Apps, configure webhooks",
      "Enter the token in the modal",
    ],
    brandColor: "#181717",
    icon: "Code",
    status: "available",
    configFields: [
      { id: "token", label: "Personal Access Token", type: "password", placeholder: "ghp_..." },
      { id: "owner", label: "Repository Owner", type: "text" },
      { id: "repo", label: "Default Repository", type: "text" },
    ],
  },
  {
    id: "wordpress",
    name: "WordPress",
    category: "content",
    tagline: "CMS and blogs",
    description:
      "Manage posts, pages, and media from your WordPress. Publish content automatically and manage comments with AI assistance.",
    features: [
      "Automatic post publishing",
      "Page and media management",
      "Comment moderation",
      "AI-assisted SEO",
      "Automatic content backup",
    ],
    setupSteps: [
      "Install the Application Passwords plugin in WP",
      "Generate an application password for the user",
      "Note the WordPress site URL",
      "Enter URL and credentials in the modal",
      "Test the connection",
    ],
    brandColor: "#21759B",
    icon: "Globe",
    status: "available",
    configFields: [
      { id: "site_url", label: "Site URL", type: "text", placeholder: "https://yoursite.com" },
      { id: "username", label: "Username", type: "text" },
      { id: "app_password", label: "Application Password", type: "password" },
    ],
  },
  {
    id: "google-sheets",
    name: "Google Sheets",
    category: "content",
    tagline: "Spreadsheets and data",
    description:
      "Read and write data in Google Sheets spreadsheets. Automate reports, dashboards, and data collection with real-time sync.",
    features: [
      "Cell reading and writing",
      "Real-time synchronization",
      "Automatic report creation",
      "Conditional formatting via API",
      "Multiple sheet support",
    ],
    setupSteps: [
      "Go to console.cloud.google.com",
      "Create a project and enable the Sheets API",
      "Create OAuth 2.0 credentials",
      "Download the credentials JSON file",
      "Enter Client ID and Secret in the modal",
    ],
    brandColor: "#34A853",
    icon: "Table",
    status: "connected",
    connectedAt: "2026-05-25T09:15:00Z",
    configFields: [
      { id: "client_id", label: "Client ID", type: "text" },
      { id: "client_secret", label: "Client Secret", type: "password" },
      { id: "spreadsheet_id", label: "Spreadsheet ID", type: "text" },
      { id: "sheet_name", label: "Sheet Name", type: "text", placeholder: "Sheet1" },
    ],
  },
];

export function getIntegrationById(id) {
  return integrations.find((i) => i.id === id);
}

export function getIntegrationsByCategory(categoryId) {
  return integrations.filter((i) => i.category === categoryId);
}

export function getConnectedIntegrations() {
  return integrations.filter((i) => i.status === "connected");
}

export function getAvailableIntegrations() {
  return integrations.filter((i) => i.status === "available");
}

export function getCategoryById(id) {
  return integrationCategories.find((c) => c.id === id);
}
